import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useGetApplicantStatusQuery,
  useGetUserDatav2Query,
} from "src/store/features/api/generated";
import { setAccountSelection, setApplicationStep } from "src/store/features/onboarding.slice";
import { useUserSlice } from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";

export interface GenericModalConfig {
  open: boolean;
  onClose: () => void;
  title: string;
  mainIssue: string;
  subIssue: string[];
  buttonText: string;
  buttonAction: () => void;
}

const useSumsubStatus = () => {
  // Check if user is a guest that has not completed applicationf form
  const { kycSteps, kycStatus } = useUserSlice();
  const dispatch = useAppDispatch();

  // The applicant-status endpoint only ever returns data for users with a
  // Sumsub (KYB/entity) applicant — the backend answers empty for everyone
  // else after a wasted vendor round-trip. Most users (e.g. an individual
  // going through onboarding) never have one, so don't call it at all unless
  // the user's record carries a Sumsub external id. getUserDatav2 is already
  // fetched app-wide, so this reads from cache.
  const { data: userData } = useGetUserDatav2Query();
  const hasSumsubApplicant = !!userData?.sumsubExternalId;

  const { data: sumsubResponse } = useGetApplicantStatusQuery(undefined, {
    skip: !hasSumsubApplicant,
  });
  const { clientComment, moderationComment, reviewStatus, reviewAnswer } = sumsubResponse || {};

  const [sumsubModalConfig, setSumsubModalConfig] = useState<GenericModalConfig | undefined>(
    undefined,
  );

  const [showPendingAlert, setShowPendingAlert] = useState(false);

  const history = useHistory();

  const handleClose = () => {
    setSumsubModalConfig((prev) => (prev ? { ...prev, open: false } : undefined));
  };

  const handleRedirect = () => {
    history.push("/entity-onboarding");
  };

  const handleFormRedirect = () => {
    dispatch(setAccountSelection("Individual"));
    dispatch(setApplicationStep("aboutYou"));
    history.push("/application");
  };

  useEffect(() => {
    // Guest user has submitted biometrics but not completed the form
    if (
      kycStatus !== "Verified" &&
      kycSteps.biometrics === true &&
      kycSteps.newApplicationForm === false
    ) {
      const config = {
        open: true,
        onClose: handleClose,
        title: "Additional information required",
        mainIssue:
          "We need more information to complete your application. Please submit the questionnaire by clicking the button below.",
        subIssue: [],
        buttonText: "Click here to continue",
        buttonAction: handleFormRedirect,
      };
      setSumsubModalConfig(config);
    }

    // User has submitted Sumsub KYB docs that have been reviewed and rejected by Sumsub
    if (reviewStatus === "completed" && (clientComment || moderationComment)) {
      const treatedModerationComment = moderationComment
        ?.split("-")
        .map((comment: string) => comment.trim().charAt(0).toUpperCase() + comment.trim().slice(1));

      const config = {
        open: true,
        onClose: handleClose,
        title: "Additional Documents Required",
        mainIssue: clientComment || "Additional document is required",
        subIssue: treatedModerationComment || [],
        buttonText: "Action Required",
        buttonAction: handleRedirect,
      };
      setSumsubModalConfig(config);
    }
  }, [reviewStatus, kycStatus, kycSteps]);

  useEffect(() => {
    // Activates a Sumsub Pending Account Alert if the account is prechecked or onHold and is a company account
    const isPrecheckedOrOnHold =
      sumsubResponse?.reviewStatus === "prechecked" || sumsubResponse?.reviewStatus === "onHold";
    const isCompany = sumsubResponse?.type === "company";

    if (isPrecheckedOrOnHold && isCompany) {
      setShowPendingAlert(true);
    } else {
      setShowPendingAlert(false);
    }
  }, [sumsubResponse]);

  return {
    sumsubModalConfig,
    showPendingAlert,
    reviewAnswer,
  };
};

export default useSumsubStatus;
